import fetch from 'isomorphic-fetch'

import railsPostFetch, { getCSRFParam, getCSRFToken } from '@aytm/lib/common/rails-post-fetch'
import { headersFromGoogleUploadUrl } from '@aytm/lib/common/google-cloud-url'

import ProgressivePromise from '@aytm/lib/AsyncJobManager/ProgressivePromise'
import uploadWithProgress from '@aytm/lib/common/upload-with-progress'

// Default adapter — Rails-native upload contract used by callers that POST to a
// Rails route returning { upload_urls: { binary_upload_url, finish_upload_url, status_upload_url } }
// and poll with status strings 'ready' / 'processing'.
export const railsAdapter = {
  createAsset: ({ file, uploadUrl, params }) =>
    railsPostFetch(uploadUrl, {
      body: JSON.stringify({
        ...params,
        [getCSRFParam()]: getCSRFToken(),
        filename: file.name,
        filesize: file.size,
        content_type: file.type
      })
    })
      .then(response => (response.ok ? response.json() : {}))
      .then(data => {
        if (!data || !data.upload_urls) {
          return Promise.reject(new Error('AssetUploadJob - invalid response data'))
        }
        return {
          uploadUrl: data.upload_urls.binary_upload_url,
          finishUrl: data.upload_urls.finish_upload_url,
          statusUrl: data.upload_urls.status_upload_url,
          asset: data
        }
      }),

  finishUpload: ({ finishUrl, asset }) =>
    fetch(finishUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCSRFToken()
      },
      Accept: 'application/json',
      body: JSON.stringify({ [getCSRFParam()]: getCSRFToken() })
    }).then(() => ({ asset })),

  checkStatus: ({ statusUrl }) =>
    fetch(statusUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCSRFToken()
      },
      Accept: 'application/json',
      body: JSON.stringify({ [getCSRFParam()]: getCSRFToken() })
    }).then(response => (response.ok ? response.json() : {})),

  terminalStatus: 'ready',
  processingStatus: 'processing',
  buildResult: response => response
}

// Storage refuses the PUT unless every header the URL signed is sent with it. A URL may sign a
// header without giving its value -- the stat server signs the content type that way -- and the
// value is then ours to send; one it does give replaces ours. Either way each name goes out once:
// the browser merges two spellings of one name into a single comma-joined value, which no longer
// matches the signature.
const signedPutHeaders = (url, contentType) => {
  const headers = { 'Content-Type': contentType }
  Object.entries(headersFromGoogleUploadUrl(url)).forEach(([name, value]) => {
    if (null == value) return
    if ('content-type' === name.toLowerCase()) delete headers['Content-Type']
    headers[name] = value
  })
  return headers
}

export default function FileUpload({
  file,
  uploadUrl,
  params = {},
  abortSignal,
  contentType,
  adapter = railsAdapter
} = {}) {
  return new ProgressivePromise((resolve, reject, progress) => {
    const totalProgress = file.size
    progress(0, totalProgress) // set progress to 0

    const ctx = { file, uploadUrl, params, abortSignal }
    let asset = null
    let statusUrl = null

    adapter
      .createAsset(ctx)

      // UPLOAD ASSET
      .then(created => {
        asset = created.asset ?? null
        statusUrl = created.statusUrl ?? null

        return uploadWithProgress(
          created.uploadUrl,
          {
            method: 'PUT',
            headers: signedPutHeaders(created.uploadUrl, contentType || file.type),
            body: file,
            rejectOnHttpError: true
          },
          progressEvent => {
            progress(Math.round(progressEvent.loaded * 0.9), totalProgress) // set progress for uploaded bytes with correction 100% -> 90%
          }
        ).then(() => created.finishUrl)
      })

      // FINISH UPLOAD
      .then(finishUrl =>
        adapter.finishUpload({ ...ctx, finishUrl, asset }).then(finished => {
          asset = finished?.asset ?? asset
          if (finished?.statusUrl) statusUrl = finished.statusUrl
        })
      )

      // CHECK STATUS
      .then(() => {
        progress(Math.round(file.size * 0.95), totalProgress) // set progress to 95%
        return new Promise((_resolve, _reject) => {
          const checkStatus = () => {
            if (abortSignal?.aborted) {
              _reject(new Error('Aborted'))
              return
            }
            adapter
              .checkStatus({ ...ctx, statusUrl, asset })
              .then(response => {
                if (abortSignal?.aborted) {
                  _reject(new Error('Aborted'))
                  return
                }
                switch (response.status) {
                  case adapter.terminalStatus: {
                    progress(totalProgress, totalProgress) // set progress to 100%
                    _resolve(adapter.buildResult(response, asset))
                    break
                  }
                  case adapter.processingStatus: {
                    setTimeout(checkStatus, 1000)
                    break
                  }
                  default: {
                    _reject(response)
                    break
                  }
                }
              })
              .catch(_error => {
                if (!abortSignal?.aborted) {
                  setTimeout(checkStatus, 1000)
                }
              })
          }
          setTimeout(checkStatus, 500)
        })
      })
      .then(resolve, reject)
      .catch(error => reject(error))
  })
}
